import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { AppMainHeader } from "@/components/AppMainHeader";
import { MobileBackBar } from "@/components/MobileBackBar";
import { useAuth } from "@/contexts/AuthContext";
import {
  applyMailAction,
  composeMail,
  createMailAccount,
  deleteMailAccount,
  getMailAttachmentBlob,
  getMailAccounts,
  getMailAccountsUnreadCounts,
  getMailFolders,
  getMailMessage,
  getMailMessages,
  getMailRecipientSuggestions,
  updateMailAccount,
  updateMailSettings,
} from "@/lib/api";
import { Archive, ChevronDown, ChevronUp, Forward, Loader2, MailPlus, Paperclip, Pin, PinOff, RefreshCw, Reply, ReplyAll, ShieldAlert, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import EmojiPicker from "emoji-picker-react";

const DEFAULT_FOLDERS = ["INBOX", "ANCLADOS", "BORRADORES", "ENVIADOS", "PAPELERA", "SPAM", "ARCHIVADOS"];

const FOLDER_NAMES_ES = {
  inbox: "Bandeja de entrada",
  sent: "Enviados",
  drafts: "Borradores",
  trash: "Papelera",
  junk: "Spam",
  spam: "Spam",
  archive: "Archivo",
  archives: "Archivo",
  archived: "Archivo",
  flagged: "Destacados",
  starred: "Destacados",
  important: "Importantes",
  all: "Todos",
  "all mail": "Todos",
  notes: "Notas",
};

function folderDisplayName(raw = "") {
  const stripped = raw.replace(/^INBOX[./]/i, "").trim();
  const key = stripped.toLowerCase();
  if (FOLDER_NAMES_ES[key]) return FOLDER_NAMES_ES[key];
  if (raw.toUpperCase() === "INBOX") return "Bandeja de entrada";
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

function escapeHtml(s = "") {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function linkifyPlainText(text = "") {
  const escaped = escapeHtml(text);
  const withLinks = escaped.replace(
    /(https?:\/\/[^\s<]+)/gi,
    (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );
  return withLinks.replace(/\n/g, "<br/>");
}

function sanitizeEmailHtml(html = "") {
  // Evita que estilos globales del correo afecten a toda la app.
  return String(html || "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<link[^>]*rel=["']?stylesheet["']?[^>]*>/gi, "")
    .replace(/<\/?(html|head|body)[^>]*>/gi, "");
}

function senderInitials(from = "", fromEmail = "") {
  const source = String(from || fromEmail || "").trim();
  if (!source) return "?";
  const cleaned = source.replace(/["<>]/g, " ").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }
  return (parts[0]?.slice(0, 2) || "?").toUpperCase();
}

function senderDomain(fromEmail = "", from = "") {
  const raw = `${fromEmail || ""} ${from || ""}`;
  const match = raw.match(/[A-Za-z0-9._%+-]+@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/);
  return match?.[1]?.toLowerCase() || "";
}

const MAX_MAIL_ATTACHMENT_BYTES = 15 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== "string") {
        reject(new Error("lectura"));
        return;
      }
      const i = dataUrl.indexOf(",");
      resolve(i >= 0 ? dataUrl.slice(i + 1) : "");
    };
    reader.onerror = () => reject(new Error("lectura"));
    reader.readAsDataURL(file);
  });
}

/** Sugerencias solo al escribir en el último fragmento (tras comas); sin texto no hay lista. */
function RecipientAutocompleteField({
  accountId,
  dialogOpen,
  value,
  onChange,
  placeholder,
}) {
  const inputRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(-1);

  const partial = useMemo(() => (value.split(",").pop() || "").trim(), [value]);

  useEffect(() => {
    if (!accountId || !dialogOpen || partial.length === 0) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      getMailRecipientSuggestions(accountId, partial)
        .then((d) => setSuggestions(d.suggestions || []))
        .catch(() => setSuggestions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [accountId, dialogOpen, partial]);

  useEffect(() => {
    setHighlight(-1);
  }, [value, suggestions.length]);

  const showList = partial.length > 0 && suggestions.length > 0;

  const applySuggestion = (email) => {
    const parts = value.split(",");
    parts[parts.length - 1] = ` ${email}`;
    const next = parts.map((p) => p.trim()).filter(Boolean).join(", ");
    onChange(next ? `${next}, ` : "");
    setSuggestions([]);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const onKeyDown = (e) => {
    if (!showList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h < 0 ? 0 : Math.min(h + 1, suggestions.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h < 0 ? suggestions.length - 1 : Math.max(h - 1, 0)));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      applySuggestion(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setSuggestions([]);
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          setTimeout(() => setSuggestions([]), 200);
        }}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <ul
          className="absolute left-0 right-0 top-full z-[100] mt-0.5 max-h-64 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md py-1"
          role="listbox"
        >
          {suggestions.map((s, i) => (
            <li key={s} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                className={`w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground ${i === highlight ? "bg-accent text-accent-foreground" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  applySuggestion(s);
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MailAttachmentPicker({ files, onAdd, onRemove, disabled }) {
  const inputRef = useRef(null);
  const pick = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of list) {
      if (f.size > MAX_MAIL_ATTACHMENT_BYTES) {
        toast.error(`«${f.name}» supera 15 MB`);
        continue;
      }
      onAdd(f);
    }
  };
  return (
    <div className="space-y-2">
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {files.map((item, i) => (
            <li
              key={item.id}
              className="flex items-center gap-1 rounded-md border border-input bg-muted/40 px-2 py-1 text-xs max-w-full"
            >
              <span className="truncate max-w-[220px]" title={item.file.name}>{item.file.name}</span>
              <button
                type="button"
                className="shrink-0 rounded p-0.5 hover:bg-muted"
                onClick={() => onRemove(i)}
                disabled={disabled}
                aria-label="Quitar adjunto"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <input ref={inputRef} type="file" multiple className="sr-only" onChange={pick} aria-hidden />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          <Paperclip className="h-4 w-4 shrink-0" />
          Adjuntar archivos
        </Button>
      </div>
    </div>
  );
}

function RichTextEditor({ value, onChange, placeholder = "Escribe..." }) {
  const editorRef = useRef(null);

  // Inicializar contenido solo una vez al montar
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resetear cuando el padre vacía el value (nueva redacción)
  useEffect(() => {
    if (editorRef.current && (!value || value === "<p></p>" || value === "")) {
      editorRef.current.innerHTML = "";
    }
  }, [value]);

  // onMouseDown + preventDefault mantiene el foco/selección en el editor
  const cmd = (command, arg = null) => (e) => {
    e.preventDefault();
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
  };

  const addLink = (e) => {
    e.preventDefault();
    editorRef.current?.focus();
    const url = window.prompt("Introduce la URL", "https://");
    if (url) document.execCommand("createLink", false, url);
  };

  return (
    <div className="border rounded-md overflow-hidden">
      <div className="border-b bg-muted/40 px-2 py-1 flex flex-wrap gap-1">
        <Button type="button" variant="ghost" size="sm" onMouseDown={cmd("formatBlock", "h1")}>H1</Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={cmd("formatBlock", "h2")}>H2</Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={cmd("formatBlock", "h3")}>H3</Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={cmd("bold")}><b>B</b></Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={cmd("italic")}><i>I</i></Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={cmd("underline")}><u>U</u></Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={cmd("insertUnorderedList")}>Lista</Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={cmd("formatBlock", "p")}>Normal</Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={addLink}>URL</Button>
      </div>
      <div
        ref={editorRef}
        className="min-h-44 p-3 outline-none text-sm [&_a]:text-blue-600 [&_a]:underline [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        data-placeholder={placeholder}
      />
    </div>
  );
}

function AddAccountDialog({ open, onOpenChange, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [emoji, setEmoji] = useState("📧");
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email_address: "",
    smtp_host: "",
    smtp_port: "465",
    smtp_user: "",
    smtp_password: "",
    smtp_use_ssl: true,
    imap_host: "",
    imap_port: "993",
    imap_user: "",
    imap_password: "",
    imap_use_ssl: true,
  });

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async () => {
    if (!form.name.trim() || !form.email_address.trim()) return toast.error("Completa nombre y email");
    setSaving(true);
    try {
      const created = await createMailAccount({
        name: form.name.trim(),
        email_address: form.email_address.trim(),
        smtp: {
          host: form.smtp_host.trim(),
          port: Number(form.smtp_port) || 465,
          user: form.smtp_user.trim(),
          password: form.smtp_password,
          use_ssl: !!form.smtp_use_ssl,
        },
        imap: {
          host: form.imap_host.trim(),
          port: Number(form.imap_port) || 993,
          user: form.imap_user.trim(),
          password: form.imap_password,
          use_ssl: !!form.imap_use_ssl,
        },
      });
      const createdId = created?.account?.id;
      if (createdId) {
        await updateMailSettings(createdId, { emoji });
      }
      toast.success("Cuenta configurada correctamente");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la cuenta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-11 h-11 shrink-0 rounded-lg bg-muted/50 flex items-center justify-center"
                  aria-label="Elegir emoji"
                >
                  <span className="text-2xl leading-none select-none inline-block" style={{ textShadow: "rgba(0, 0, 0, 0.3) 0px 1px 2px" }}>
                    {emoji || "📧"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border-0" align="start" onWheel={(e) => e.stopPropagation()}>
                <EmojiPicker
                  onEmojiClick={(emojiData) => {
                    setEmoji(emojiData.emoji);
                    setEmojiPickerOpen(false);
                  }}
                  theme="light"
                />
              </PopoverContent>
            </Popover>
            <div>
              <DialogTitle>Añadir cuenta</DialogTitle>
              <DialogDescription>Configura conexión SMTP e IMAP para esta cuenta.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Nombre visible</Label>
            <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Ej. Soporte" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={form.email_address} onChange={(e) => setField("email_address", e.target.value)} placeholder="correo@dominio.com" />
          </div>
          <div className="space-y-2">
            <Label>SMTP host</Label>
            <Input value={form.smtp_host} onChange={(e) => setField("smtp_host", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>SMTP puerto</Label>
            <Input value={form.smtp_port} onChange={(e) => setField("smtp_port", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>SMTP usuario</Label>
            <Input value={form.smtp_user} onChange={(e) => setField("smtp_user", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>SMTP contraseña</Label>
            <Input type="password" value={form.smtp_password} onChange={(e) => setField("smtp_password", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>IMAP host</Label>
            <Input value={form.imap_host} onChange={(e) => setField("imap_host", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>IMAP puerto</Label>
            <Input value={form.imap_port} onChange={(e) => setField("imap_port", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>IMAP usuario</Label>
            <Input value={form.imap_user} onChange={(e) => setField("imap_user", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>IMAP contraseña</Label>
            <Input type="password" value={form.imap_password} onChange={(e) => setField("imap_password", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Guardar cuenta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountSettingsModal({ account, open, onOpenChange, onSaved, onDelete }) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [name, setName] = useState(account?.name || "");
  const [signature, setSignature] = useState(account?.settings?.signature_html || "");
  const [emailAddress, setEmailAddress] = useState(account?.email_address || "");
  const [smtpHost, setSmtpHost] = useState(account?.smtp?.host || "");
  const [smtpPort, setSmtpPort] = useState(String(account?.smtp?.port || 465));
  const [smtpUser, setSmtpUser] = useState(account?.smtp?.user || "");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [imapHost, setImapHost] = useState(account?.imap?.host || "");
  const [imapPort, setImapPort] = useState(String(account?.imap?.port || 993));
  const [imapUser, setImapUser] = useState(account?.imap?.user || "");
  const [imapPassword, setImapPassword] = useState("");
  const [emoji, setEmoji] = useState(account?.settings?.emoji || "📧");
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const lockedConnection = !!account?.has_sent_mail;

  useEffect(() => {
    setName(account?.name || "");
    setSignature(account?.settings?.signature_html || "");
    setEmailAddress(account?.email_address || "");
    setSmtpHost(account?.smtp?.host || "");
    setSmtpPort(String(account?.smtp?.port || 465));
    setSmtpUser(account?.smtp?.user || "");
    setSmtpPassword("");
    setImapHost(account?.imap?.host || "");
    setImapPort(String(account?.imap?.port || 993));
    setImapUser(account?.imap?.user || "");
    setImapPassword("");
    setEmoji(account?.settings?.emoji || "📧");
  }, [account]);

  const save = async () => {
    if (!account?.id) return;
    setSaving(true);
    try {
      if (!lockedConnection) {
        await updateMailAccount(account.id, {
          name,
          email_address: emailAddress,
          smtp: {
            host: smtpHost,
            port: Number(smtpPort) || 465,
            user: smtpUser,
            password: smtpPassword || "",
            use_ssl: true,
          },
          imap: {
            host: imapHost,
            port: Number(imapPort) || 993,
            user: imapUser,
            password: imapPassword || "",
            use_ssl: true,
          },
        });
      }
      await updateMailSettings(account.id, { signature_html: signature, emoji });
      toast.success("Configuración guardada");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const removeAccount = async () => {
    if (!account?.id) return;
    if (!window.confirm("¿Seguro que quieres eliminar esta cuenta de correo? Esta acción no se puede deshacer.")) return;
    setDeleting(true);
    try {
      await onDelete?.(account.id);
      toast.success("Cuenta eliminada");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar la cuenta");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Opciones de cuenta</DialogTitle>
          <DialogDescription>Configura conexión, firma y preferencias de envío.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="conexion">
          <TabsList>
            <TabsTrigger value="conexion">Conexión</TabsTrigger>
            <TabsTrigger value="firma">Firma HTML</TabsTrigger>
            <TabsTrigger value="preferencias">Preferencias</TabsTrigger>
          </TabsList>
          <TabsContent value="conexion" className="space-y-2">
            {lockedConnection && (
              <p className="text-xs text-amber-600">
                Esta cuenta ya ha enviado correos. La configuración de conexión está bloqueada.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Nombre visible</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} disabled={lockedConnection} />
              </div>
              <div className="space-y-1">
                <Label>Email remitente</Label>
                <Input value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} disabled={lockedConnection} />
              </div>
              <div className="space-y-1">
                <Label>SMTP host</Label>
                <Input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} disabled={lockedConnection} />
              </div>
              <div className="space-y-1">
                <Label>SMTP puerto</Label>
                <Input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} disabled={lockedConnection} />
              </div>
              <div className="space-y-1">
                <Label>SMTP usuario</Label>
                <Input value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} disabled={lockedConnection} />
              </div>
              <div className="space-y-1">
                <Label>SMTP contraseña</Label>
                <Input type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} disabled={lockedConnection} placeholder="Dejar en blanco para mantener" />
              </div>
              <div className="space-y-1">
                <Label>IMAP host</Label>
                <Input value={imapHost} onChange={(e) => setImapHost(e.target.value)} disabled={lockedConnection} />
              </div>
              <div className="space-y-1">
                <Label>IMAP puerto</Label>
                <Input value={imapPort} onChange={(e) => setImapPort(e.target.value)} disabled={lockedConnection} />
              </div>
              <div className="space-y-1">
                <Label>IMAP usuario</Label>
                <Input value={imapUser} onChange={(e) => setImapUser(e.target.value)} disabled={lockedConnection} />
              </div>
              <div className="space-y-1">
                <Label>IMAP contraseña</Label>
                <Input type="password" value={imapPassword} onChange={(e) => setImapPassword(e.target.value)} disabled={lockedConnection} placeholder="Dejar en blanco para mantener" />
              </div>
            </div>
          </TabsContent>
          <TabsContent value="firma" className="space-y-2">
            <Label>Firma (HTML)</Label>
            <Textarea value={signature} onChange={(e) => setSignature(e.target.value)} rows={10} />
          </TabsContent>
          <TabsContent value="preferencias" className="text-sm text-muted-foreground">
            <div className="space-y-2">
              <Label>Emoji de la cuenta</Label>
              <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-11 h-11 rounded-lg bg-muted/50 flex items-center justify-center"
                    aria-label="Elegir emoji"
                  >
                    <span className="text-2xl leading-none select-none inline-block" style={{ textShadow: "rgba(0, 0, 0, 0.3) 0px 1px 2px" }}>
                      {emoji || "📧"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-0" align="start" onWheel={(e) => e.stopPropagation()}>
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      setEmoji(emojiData.emoji);
                      setEmojiPickerOpen(false);
                    }}
                    theme="light"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="destructive" onClick={removeAccount} disabled={deleting || saving}>
            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Eliminar cuenta
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ComposeMailDialog({ open, onOpenChange, account, onSent }) {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ to: "", cc: "", bcc: "", subject: "", body: "" });
  const [attachmentItems, setAttachmentItems] = useState([]);
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm({ to: "", cc: "", bcc: "", subject: "", body: "" });
      setAttachmentItems([]);
    }
  }, [open]);

  const addAttachment = (file) => {
    setAttachmentItems((prev) => [...prev, { id: crypto.randomUUID(), file }]);
  };

  const removeAttachment = (index) => {
    setAttachmentItems((prev) => prev.filter((_, i) => i !== index));
  };

  const send = async () => {
    if (!account?.id) return;
    if (!form.to.trim()) return toast.error("Indica al menos un destinatario");
    setSending(true);
    try {
      const attachments = await Promise.all(
        attachmentItems.map(async ({ file }) => ({
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          content_base64: await fileToBase64(file),
        }))
      );
      await composeMail(account.id, {
        to: form.to.split(",").map((v) => v.trim()).filter(Boolean),
        cc: form.cc.split(",").map((v) => v.trim()).filter(Boolean),
        bcc: form.bcc.split(",").map((v) => v.trim()).filter(Boolean),
        subject: form.subject,
        html_body: form.body,
        attachments,
      });
      toast.success("Correo enviado");
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crear correo</DialogTitle>
          <DialogDescription>Redacta un nuevo correo para la cuenta seleccionada.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Asunto" value={form.subject} onChange={(e) => setField("subject", e.target.value)} />
          <RecipientAutocompleteField
            accountId={account?.id}
            dialogOpen={open}
            value={form.to}
            onChange={(v) => setField("to", v)}
            placeholder="Para (separa por comas)"
          />
          <RecipientAutocompleteField
            accountId={account?.id}
            dialogOpen={open}
            value={form.cc}
            onChange={(v) => setField("cc", v)}
            placeholder="Cc (separa por comas)"
          />
          <RecipientAutocompleteField
            accountId={account?.id}
            dialogOpen={open}
            value={form.bcc}
            onChange={(v) => setField("bcc", v)}
            placeholder="Cco (separa por comas)"
          />
          <MailAttachmentPicker
            files={attachmentItems}
            onAdd={addAttachment}
            onRemove={removeAttachment}
            disabled={sending}
          />
          <RichTextEditor value={form.body} onChange={(v) => setField("body", v)} placeholder="Escribe el correo..." />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={send} disabled={sending}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReplyMailDialog({ open, onOpenChange, account, message, mode = "reply", onSent }) {
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachmentItems, setAttachmentItems] = useState([]);

  const extractEmails = (raw = "") => {
    const m = raw.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g);
    return (m || []).map((e) => e.trim().toLowerCase());
  };

  const accountEmail = (account?.email_address || "").trim().toLowerCase();
  const historyHtml = `
    <hr />
    <p><b>De:</b> ${message?.from || "-"}</p>
    <p><b>Para:</b> ${message?.to || "-"}</p>
    <p><b>Fecha:</b> ${message?.date || "-"}</p>
    <p><b>Asunto:</b> ${message?.subject || "-"}</p>
    <div>${message?.html_body || message?.text_body || ""}</div>
  `;

  useEffect(() => {
    if (!open) return;
    const from = extractEmails(message?.from || "");
    const toList = extractEmails(message?.to || "");
    const ccList = extractEmails(message?.cc || "");
    const dedupe = (arr) => Array.from(new Set(arr.filter(Boolean)));
    if (mode === "reply_all") {
      const replyAllTo = dedupe([...from, ...toList].filter((e) => e !== accountEmail));
      const replyAllCc = dedupe(ccList.filter((e) => e !== accountEmail && !replyAllTo.includes(e)));
      setTo(replyAllTo.join(", "));
      setCc(replyAllCc.join(", "));
      setSubject((message?.subject || "").toLowerCase().startsWith("re:") ? (message?.subject || "") : `Re: ${message?.subject || ""}`);
    } else if (mode === "forward") {
      setTo("");
      setCc("");
      setSubject((message?.subject || "").toLowerCase().startsWith("fwd:") ? (message?.subject || "") : `Fwd: ${message?.subject || ""}`);
    } else {
      setTo(from[0] || "");
      setCc("");
      setSubject((message?.subject || "").toLowerCase().startsWith("re:") ? (message?.subject || "") : `Re: ${message?.subject || ""}`);
    }
    setBcc("");
    setBody("");
    setAttachmentItems([]);
  }, [open, message?.id, mode, accountEmail]);

  const addAttachment = (file) => {
    setAttachmentItems((prev) => [...prev, { id: crypto.randomUUID(), file }]);
  };

  const removeAttachment = (index) => {
    setAttachmentItems((prev) => prev.filter((_, i) => i !== index));
  };

  const sendReply = async () => {
    if (!account?.id || !to.trim()) return;
    setSending(true);
    try {
      const attachments = await Promise.all(
        attachmentItems.map(async ({ file }) => ({
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          content_base64: await fileToBase64(file),
        }))
      );
      await composeMail(account.id, {
        to: to.split(",").map((v) => v.trim()).filter(Boolean),
        cc: cc.split(",").map((v) => v.trim()).filter(Boolean),
        bcc: bcc.split(",").map((v) => v.trim()).filter(Boolean),
        subject,
        html_body: `${body}<br/><br/>${historyHtml}`,
        attachments,
      });
      toast.success("Respuesta enviada");
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la respuesta");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "reply_all" ? "Responder a todos" : mode === "forward" ? "Reenviar" : "Responder"}</DialogTitle>
          <DialogDescription>
            {mode === "forward" ? "Reenvía este correo con su historial." : "Responde con el historial del correo incluido."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <RecipientAutocompleteField
            accountId={account?.id}
            dialogOpen={open}
            value={to}
            onChange={setTo}
            placeholder="Para (separa por comas)"
          />
          <RecipientAutocompleteField
            accountId={account?.id}
            dialogOpen={open}
            value={cc}
            onChange={setCc}
            placeholder="Cc (separa por comas)"
          />
          <RecipientAutocompleteField
            accountId={account?.id}
            dialogOpen={open}
            value={bcc}
            onChange={setBcc}
            placeholder="Cco (separa por comas)"
          />
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Asunto" />
          <MailAttachmentPicker
            files={attachmentItems}
            onAdd={addAttachment}
            onRemove={removeAttachment}
            disabled={sending}
          />
          <RichTextEditor value={body} onChange={setBody} placeholder="Escribe tu respuesta..." />
          <div className="border rounded-md p-3 bg-muted/30 max-h-64 overflow-auto text-sm">
            <div className="font-medium mb-2">Historial</div>
            <div dangerouslySetInnerHTML={{ __html: historyHtml }} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={sendReply} disabled={sending || !to.trim()}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {mode === "forward" ? "Reenviar" : mode === "reply_all" ? "Enviar respuesta a todos" : "Enviar respuesta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CorreosLayout() {
  const { user, authLoading } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedFolder, setSelectedFolder] = useState("INBOX");
  const [folders, setFolders] = useState(DEFAULT_FOLDERS);
  const [messages, setMessages] = useState([]);
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyMode, setReplyMode] = useState("reply");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsAccount, setSettingsAccount] = useState(null);
  const [unreadByAccount, setUnreadByAccount] = useState({});
  const [inboxTab, setInboxTab] = useState("all");
  const [inboxSearch, setInboxSearch] = useState("");
  const [inboxSearchTerms, setInboxSearchTerms] = useState([]);
  const [showAllRecipients, setShowAllRecipients] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [batchSelectedIds, setBatchSelectedIds] = useState(new Set());
  // Móvil: las tres columnas son pantallas encadenadas (en escritorio se ignora)
  const [mobilePane, setMobilePane] = useState("accounts"); // accounts | list | message
  useEffect(() => {
    if (mobilePane === "message" && !selectedMessageId) setMobilePane("list");
  }, [mobilePane, selectedMessageId]);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [attachmentObjectUrls, setAttachmentObjectUrls] = useState({});
  const [pendingActionCount, setPendingActionCount] = useState(0);
  const attachmentsSectionRef = useRef(null);
  const [headerExpanded, setHeaderExpanded] = useState(false);
  const [accountOrderIds, setAccountOrderIds] = useState(() => {
    try {
      const raw = localStorage.getItem("mail-account-order-ids");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });
  const [dragAccountId, setDragAccountId] = useState("");
  const listScrollRef = useRef(null);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId) || null,
    [accounts, selectedAccountId]
  );
  const orderedAccounts = useMemo(() => {
    if (!accounts?.length) return [];
    if (!accountOrderIds?.length) return accounts;
    const byId = new Map(accounts.map((a) => [a.id, a]));
    const ordered = [];
    for (const id of accountOrderIds) {
      if (byId.has(id)) ordered.push(byId.get(id));
    }
    for (const acc of accounts) {
      if (!accountOrderIds.includes(acc.id)) ordered.push(acc);
    }
    return ordered;
  }, [accounts, accountOrderIds]);

  const persistAccountOrder = useCallback((ids) => {
    setAccountOrderIds(ids);
    try {
      localStorage.setItem("mail-account-order-ids", JSON.stringify(ids));
    } catch (_) {}
  }, []);

  const reloadAccounts = async () => {
    try {
      const data = await getMailAccounts();
      const list = data.accounts || [];
      setAccounts(list);
      const listIds = list.map((a) => a.id);
      if (!accountOrderIds.length) {
        persistAccountOrder(listIds);
      } else {
        const cleaned = accountOrderIds.filter((id) => listIds.includes(id));
        const missing = listIds.filter((id) => !cleaned.includes(id));
        persistAccountOrder([...cleaned, ...missing]);
      }
      const orderedIds = accountOrderIds.length
        ? [...accountOrderIds.filter((id) => listIds.includes(id)), ...listIds.filter((id) => !accountOrderIds.includes(id))]
        : listIds;
      try {
        const d = await getMailAccountsUnreadCounts();
        setUnreadByAccount(d.unread_counts || {});
      } catch (_) {
        const unreadMap = {};
        await Promise.all(
          list.map(async (acc) => {
            try {
              const d = await getMailMessages(acc.id, { folder: "INBOX", page: 1, pageSize: 1 });
              unreadMap[acc.id] = Number(d.unread_count || 0);
            } catch (_) {
              unreadMap[acc.id] = 0;
            }
          })
        );
        setUnreadByAccount(unreadMap);
      }
      if (!selectedAccountId && orderedIds.length) {
        setSelectedAccountId(orderedIds[0]);
        setSelectedFolder("INBOX");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron cargar las cuentas");
    }
  };

  useEffect(() => {
    if (!user || user?.permissions?.correos === false) return;
    reloadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Activar/desactivar modo batch con Espacio (ignorar cuando el foco está en un input)
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== " ") return;
      const tag = e.target?.tagName || "";
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target?.isContentEditable) return;
      e.preventDefault();
      setBatchMode((prev) => {
        if (prev) setBatchSelectedIds(new Set());
        return !prev;
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const executeBatchAction = useCallback(async (action) => {
    if (!selectedAccountId || batchSelectedIds.size === 0) return;
    const ids = Array.from(batchSelectedIds);
    setPendingActionCount((n) => n + 1);
    try {
      await Promise.all(ids.map((id) =>
        applyMailAction(selectedAccountId, id, { action, folder: selectedFolder })
      ));
      if (action === "mark_unread") {
        setMessages((prev) => prev.map((m) => batchSelectedIds.has(m.id) ? { ...m, unread: true } : m));
      } else if (action === "flag") {
        setMessages((prev) => prev.map((m) => batchSelectedIds.has(m.id) ? { ...m, flagged: true } : m));
      } else if (action === "unflag") {
        setMessages((prev) => prev.map((m) => batchSelectedIds.has(m.id) ? { ...m, flagged: false } : m));
      } else {
        const refreshed = await getMailMessages(selectedAccountId, { folder: selectedFolder });
        setMessages(refreshed.messages || []);
      }
      toast.success(`Acción aplicada a ${ids.length} correo${ids.length !== 1 ? "s" : ""}`);
      setBatchSelectedIds(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo aplicar la acción");
    } finally {
      setPendingActionCount((n) => Math.max(0, n - 1));
    }
  }, [selectedAccountId, selectedFolder, batchSelectedIds]);

  useEffect(() => {
    if (!selectedAccountId) return;
    getMailFolders(selectedAccountId)
      .then((d) => {
        const remote = (d.folders || []).map((f) => f.name).filter(Boolean);
        // Usar únicamente carpetas reales del servidor IMAP para que SELECT funcione
        setFolders(remote.length > 0 ? remote : DEFAULT_FOLDERS);
      })
      .catch(() => setFolders(DEFAULT_FOLDERS));
  }, [selectedAccountId]);

  // Si la carpeta guardada no existe en esta cuenta (p. ej. tras cambiar de cuenta), usar una válida.
  useEffect(() => {
    if (!selectedAccountId || folders.length === 0) return;
    if (folders.includes(selectedFolder)) return;
    const alt = folders.find((f) => f.toLowerCase() === selectedFolder.toLowerCase());
    setSelectedFolder(alt || folders[0]);
  }, [selectedAccountId, folders, selectedFolder]);

  useEffect(() => {
    if (!selectedAccountId) return;
    setLoadingMessages(true);
    setMessagesPage(1);
    setHasMoreMessages(true);
    getMailMessages(selectedAccountId, { folder: selectedFolder, page: 1, pageSize: 30 })
      .then((d) => {
        const list = d.messages || [];
        setMessages(list);
        setHasMoreMessages(list.length >= 30);
        if (list.length > 0) setSelectedMessageId(list[0].id);
        else {
          setSelectedMessageId("");
          setSelectedMessage(null);
        }
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "No se pudieron cargar los correos"))
      .finally(() => setLoadingMessages(false));
  }, [selectedAccountId, selectedFolder]);

  useEffect(() => {
    if (!selectedAccountId || !selectedMessageId) return;
    setHeaderExpanded(false);
    setShowAllRecipients(false);
    // El backend marca \Seen automáticamente al obtener el mensaje (modo escritura + RFC822)
    getMailMessage(selectedAccountId, selectedMessageId, selectedFolder)
      .then((d) => {
        setSelectedMessage(d.message || null);
        // Reflejar inmediatamente en UI, pero la fuente real es IMAP
        setMessages((prev) => prev.map((m) => (m.id === selectedMessageId ? { ...m, unread: false } : m)));
      })
      .catch(() => setSelectedMessage(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, selectedMessageId, selectedFolder]);

  const executeAction = async (action, messageIdOverride = null) => {
    const effectiveMessageId = messageIdOverride || selectedMessageId;
    if (!selectedAccountId || !effectiveMessageId) return;
    setPendingActionCount((n) => n + 1);
    try {
      await applyMailAction(selectedAccountId, effectiveMessageId, { action, folder: selectedFolder });
      if (action === "mark_unread") {
        setMessages((prev) => prev.map((m) => (m.id === effectiveMessageId ? { ...m, unread: true } : m)));
      } else if (action === "flag") {
        setMessages((prev) => prev.map((m) => (m.id === effectiveMessageId ? { ...m, flagged: true } : m)));
      } else if (action === "unflag") {
        setMessages((prev) => prev.map((m) => (m.id === effectiveMessageId ? { ...m, flagged: false } : m)));
      } else {
        const refreshed = await getMailMessages(selectedAccountId, { folder: selectedFolder });
        setMessages(refreshed.messages || []);
      }
      toast.success("Acción aplicada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo aplicar la acción");
    } finally {
      setPendingActionCount((n) => Math.max(0, n - 1));
    }
  };

  const refreshInbox = useCallback(async ({ silent = false } = {}) => {
    if (!selectedAccountId) return;
    if (!silent) setLoadingMessages(true);
    try {
      const d = await getMailMessages(selectedAccountId, { folder: selectedFolder, page: 1, pageSize: 30 });
      const incoming = d.messages || [];
      if (silent) {
        // Refuerzo silencioso: combinar sin reducir la lista existente
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newOnes = incoming.filter((m) => !existingIds.has(m.id));
          if (!newOnes.length) {
            // actualizar flags de los mensajes ya cargados
            return prev.map((pm) => {
              const fresh = incoming.find((im) => im.id === pm.id);
              return fresh ? { ...pm, unread: fresh.unread, flagged: fresh.flagged } : pm;
            });
          }
          return [...newOnes, ...prev];
        });
      } else {
        setMessages(incoming);
        setMessagesPage(1);
        setHasMoreMessages(incoming.length >= 30);
        if (!selectedMessageId && incoming.length > 0) setSelectedMessageId(incoming[0].id);
      }
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "No se pudo actualizar el inbox");
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, [selectedAccountId, selectedFolder, selectedMessageId]);

  useEffect(() => {
    if (!selectedAccountId) return undefined;
    const timer = setInterval(() => {
      refreshInbox({ silent: true });
    }, 15000);
    return () => clearInterval(timer);
  }, [selectedAccountId, selectedFolder, refreshInbox]);

  const loadMoreMessages = useCallback(async () => {
    if (!selectedAccountId || loadingMoreMessages || loadingMessages || !hasMoreMessages) return;
    const nextPage = messagesPage + 1;
    setLoadingMoreMessages(true);
    try {
      const d = await getMailMessages(selectedAccountId, { folder: selectedFolder, page: nextPage, pageSize: 30 });
      const list = d.messages || [];
      const existing = new Set(messages.map((m) => m.id));
      const appended = list.filter((m) => !existing.has(m.id));
      setMessages((prev) => {
        const ex = new Set(prev.map((m) => m.id));
        return [...prev, ...list.filter((m) => !ex.has(m.id))];
      });
      setMessagesPage(nextPage);
      if (list.length < 30 || appended.length === 0) setHasMoreMessages(false);
    } catch (_) {
      setHasMoreMessages(false);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [
    selectedAccountId,
    loadingMoreMessages,
    loadingMessages,
    hasMoreMessages,
    messagesPage,
    selectedFolder,
    messages,
  ]);

  const handleListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el || loadingMessages || loadingMoreMessages || !hasMoreMessages) return;
    const threshold = 120;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= threshold) {
      void loadMoreMessages();
    }
  }, [hasMoreMessages, loadingMessages, loadingMoreMessages, loadMoreMessages]);

  useEffect(() => {
    // Búsqueda global: mientras haya términos activos, seguir cargando páginas hasta agotar buzón.
    if (!selectedAccountId || inboxSearchTerms.length === 0) return;
    if (loadingMessages || loadingMoreMessages || !hasMoreMessages) return;
    void loadMoreMessages();
  }, [
    selectedAccountId,
    inboxSearchTerms.length,
    loadingMessages,
    loadingMoreMessages,
    hasMoreMessages,
    loadMoreMessages,
  ]);

  const extractEmails = (raw = "") => {
    const matches = raw.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g);
    return matches || [];
  };

  const copyEmail = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Copiado: ${value}`);
    } catch (_) {
      toast.error("No se pudo copiar el email");
    }
  };

  const renderedMessageHtml = useMemo(() => {
    if (!selectedMessage) return "<p>(Sin contenido)</p>";
    if (selectedMessage.html_body && selectedMessage.html_body.trim()) return sanitizeEmailHtml(selectedMessage.html_body);
    if (selectedMessage.text_body && selectedMessage.text_body.trim()) return linkifyPlainText(selectedMessage.text_body);
    return "<p>(Sin contenido)</p>";
  }, [selectedMessage]);
  const toEmails = useMemo(() => extractEmails(selectedMessage?.to || ""), [selectedMessage]);
  const visibleToEmails = showAllRecipients ? toEmails : toEmails.slice(0, 3);
  const hiddenToCount = Math.max(0, toEmails.length - visibleToEmails.length);
  const messageAttachments = useMemo(() => (Array.isArray(selectedMessage?.attachments) ? selectedMessage.attachments : []), [selectedMessage]);

  const formatAttachmentSize = (size) => {
    const n = Number(size || 0);
    if (!Number.isFinite(n) || n <= 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatAttachmentName = (name = "") => {
    const raw = String(name || "adjunto");
    const dot = raw.lastIndexOf(".");
    const base = dot > 0 ? raw.slice(0, dot) : raw;
    if (base.length <= 18) return base;
    return `${base.slice(0, 15)}...`;
  };

  const scrollToAttachments = () => {
    attachmentsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const reorderAccounts = useCallback((fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const current = [...(accountOrderIds?.length ? accountOrderIds : accounts.map((a) => a.id))];
    const fromIdx = current.indexOf(fromId);
    const toIdx = current.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = current.splice(fromIdx, 1);
    current.splice(toIdx, 0, moved);
    persistAccountOrder(current);
  }, [accountOrderIds, accounts, persistAccountOrder]);

  const handleDeleteAccount = async (accountId) => {
    await deleteMailAccount(accountId);
    if (selectedAccountId === accountId) {
      setSelectedAccountId("");
      setSelectedMessageId("");
      setSelectedMessage(null);
    }
    await reloadAccounts();
  };

  useEffect(() => {
    return () => {
      Object.values(attachmentObjectUrls).forEach((entry) => {
        if (entry?.url) URL.revokeObjectURL(entry.url);
      });
    };
  }, [attachmentObjectUrls]);

  useEffect(() => {
    // Al cambiar de mensaje, liberar URLs de adjuntos anteriores
    Object.values(attachmentObjectUrls).forEach((entry) => {
      if (entry?.url) URL.revokeObjectURL(entry.url);
    });
    setAttachmentObjectUrls({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMessageId]);

  const ensureAttachmentObjectUrl = async (att) => {
    if (!selectedAccountId || !selectedMessageId) return;
    const key = String(att.index);
    if (attachmentObjectUrls[key]?.url) return attachmentObjectUrls[key];
    const res = await getMailAttachmentBlob(selectedAccountId, selectedMessageId, att.index, { folder: selectedFolder, download: false });
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || att.content_type || "application/octet-stream" });
    const entry = { url: URL.createObjectURL(blob), contentType: blob.type || att.content_type || "application/octet-stream" };
    setAttachmentObjectUrls((prev) => ({ ...prev, [key]: entry }));
    return entry;
  };

  const previewAttachment = async (att) => {
    try {
      const entry = await ensureAttachmentObjectUrl(att);
      if (!entry?.url) throw new Error("No URL");
      setAttachmentPreview({ url: entry.url, filename: att.filename || "adjunto", contentType: entry.contentType || att.content_type || "" });
      setPreviewOpen(true);
    } catch (_) {
      toast.error("No se pudo previsualizar el adjunto");
    }
  };

  const downloadAttachment = async (att) => {
    if (!selectedAccountId || !selectedMessageId) return;
    try {
      const res = await getMailAttachmentBlob(selectedAccountId, selectedMessageId, att.index, { folder: selectedFolder, download: true });
      const blob = new Blob([res.data], { type: res.headers?.["content-type"] || att.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "adjunto";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("No se pudo descargar el adjunto");
    }
  };

  const downloadAllAttachments = async () => {
    for (const att of messageAttachments) {
      // secuencial para evitar bloquear el navegador con demasiadas descargas simultáneas
      // eslint-disable-next-line no-await-in-loop
      await downloadAttachment(att);
    }
  };

  const messageCategory = useCallback((m) => {
    const serverCategory = (m?.category || "").toLowerCase();
    if (serverCategory === "notification" || serverCategory === "newsletter" || serverCategory === "personal") {
      return serverCategory;
    }
    const fromLower = `${m?.from || ""} ${m?.from_email || ""}`.toLowerCase();
    const subjectLower = (m?.subject || "").toLowerCase();
    if (
      fromLower.includes("noreply")
      || fromLower.includes("no-reply")
      || fromLower.includes("notification")
      || fromLower.includes("alert")
      || fromLower.includes("wordpress")
      || subjectLower.includes("notificación")
      || subjectLower.includes("notificacion")
      || subjectLower.includes("notification")
      || subjectLower.includes("alerta")
      || subjectLower.includes("alert")
      || subjectLower.includes("acceso")
      || subjectLower.includes("login")
      || subjectLower.includes("fallido")
    ) {
      return "notification";
    }
    return "personal";
  }, []);

  /** En Notificaciones/Boletines el filtro es en cliente: si en la página cargada hay pocos ítems, pedir más hasta llenar o agotar el buzón. */
  const INBOX_TAB_CATEGORY_MIN = 30;
  useEffect(() => {
    if (inboxTab !== "notifications" && inboxTab !== "newsletters") return;
    if (!selectedAccountId || loadingMessages || loadingMoreMessages || !hasMoreMessages) return;
    const want = inboxTab === "notifications" ? "notification" : "newsletter";
    const n = messages.filter((m) => messageCategory(m) === want).length;
    if (n >= INBOX_TAB_CATEGORY_MIN) return;
    void loadMoreMessages();
  }, [
    inboxTab,
    messages,
    hasMoreMessages,
    loadingMessages,
    loadingMoreMessages,
    selectedAccountId,
    loadMoreMessages,
    messageCategory,
  ]);

  const filteredMessages = useMemo(() => {
    let list = messages;
    if (inboxTab === "notifications") {
      list = list.filter((m) => messageCategory(m) === "notification");
    } else if (inboxTab === "newsletters") {
      list = list.filter((m) => messageCategory(m) === "newsletter");
    }
    // Anclados siempre al principio
    list = [...list].sort((a, b) => (b.flagged ? 1 : 0) - (a.flagged ? 1 : 0));
    // "all" (Todo) no aplica filtro de categoría — muestra todo
    if (!inboxSearchTerms.length) return list;
    return list.filter((m) => {
      const haystack = [
        m.subject || "",
        m.from || "",
        m.from_email || "",
        m.to || "",
        m.snippet || "",
        m.date || "",
      ]
        .join(" ")
        .toLowerCase();
      return inboxSearchTerms.every((term) => haystack.includes(term.toLowerCase()));
    });
  }, [messages, inboxSearchTerms, inboxTab, messageCategory]);

  const formatInboxRightDate = (raw = "") => {
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (sameDay) {
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    return d.toLocaleDateString("es-ES", { month: "short", day: "numeric" });
  };

  const formatHeaderDate = (raw = "") => {
    if (!raw) return "-";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw.replace(/\s\+\d{4}$/, "");
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}h`;
  };

  if (authLoading || !user || user?.permissions?.correos === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="Correos" />
      <div className="flex flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        <aside className={`${mobilePane === "accounts" ? "flex" : "hidden md:flex"} w-full md:w-80 shrink-0 md:border-r border-gray-300 bg-muted/20 flex-col overflow-hidden`}>
          <div className="h-14 px-4 border-b flex items-center shrink-0">
            <Button onClick={() => setAddOpen(true)} className="w-full gap-2">
              <MailPlus className="w-4 h-4" />
              Añadir cuenta
            </Button>
          </div>
          <div className="p-3 space-y-2 overflow-y-auto flex-1 mobile-nav-scroll">
            {orderedAccounts.map((account) => (
              <ContextMenu key={account.id}>
                <ContextMenuTrigger className="block w-full">
                  <Card
                    className="w-full shadow-none hover:bg-muted/50 cursor-pointer"
                    draggable
                    onDragStart={() => setDragAccountId(account.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      reorderAccounts(dragAccountId, account.id);
                      setDragAccountId("");
                    }}
                    onClick={() => {
                      setSelectedAccountId(account.id);
                      setSelectedFolder("INBOX");
                      setMobilePane("list");
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="flex gap-3">
                        <div className="w-11 h-11 shrink-0 rounded-lg bg-muted/50 flex items-center justify-center">
                          <span className="text-2xl leading-none select-none inline-block" style={{ textShadow: "rgba(0, 0, 0, 0.3) 0px 1px 2px" }}>
                            {account?.settings?.emoji || "📧"}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                          <div className="text-sm font-medium truncate flex items-center gap-2">
                            <span className="truncate flex-1">{account.name}</span>
                            {(unreadByAccount[account.id] || 0) > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-medium shrink-0">
                                {unreadByAccount[account.id]}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{account.email_address}</div>
                        </div>
                      </div>
                      {selectedAccountId === account.id && (
                        <div className="mt-2 space-y-1">
                          {(folders.length ? folders : DEFAULT_FOLDERS).map((folder) => (
                            <button
                              key={folder}
                              type="button"
                              className={`w-full text-left text-sm md:text-xs rounded px-2 py-2.5 md:py-1 ${selectedFolder === folder ? "bg-secondary" : "hover:bg-muted"}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFolder(folder);
                                setMobilePane("list");
                              }}
                            >
                              {folderDisplayName(folder)}
                            </button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <ContextMenuItem onSelect={() => { setSettingsAccount(account); setSettingsOpen(true); }}>
                    Configurar cuenta
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            ))}
          </div>
        </aside>

        <aside className={`${mobilePane === "list" ? "flex" : "hidden md:flex"} w-full md:w-96 shrink-0 md:border-r border-gray-300 bg-muted/20 flex-col overflow-hidden`}>
          <MobileBackBar onBack={() => setMobilePane("accounts")} label={folderDisplayName(selectedFolder)}>
            <Button
              type="button"
              size="sm"
              className="h-9 bg-black text-white hover:bg-black/90"
              onClick={() => setComposeOpen(true)}
              disabled={!selectedAccountId}
            >
              Nuevo
            </Button>
          </MobileBackBar>
          <div className="h-14 px-3 border-b flex items-center justify-between gap-2 shrink-0">
            <Tabs value={inboxTab} onValueChange={setInboxTab} className="min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsList className="rounded-md border border-input bg-background p-0.5 h-9">
                <TabsTrigger value="all" className="rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                  Todo
                </TabsTrigger>
                <TabsTrigger value="notifications" className="rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                  Notificaciones
                </TabsTrigger>
                <TabsTrigger value="newsletters" className="rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                  Boletines
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Button type="button" size="icon" variant="outline" className="h-10 w-10 md:h-8 md:w-8 shrink-0" onClick={() => refreshInbox()} disabled={loadingMessages} aria-label="Actualizar" title="Actualizar">
              {loadingMessages ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
          <div className="h-14 px-3 border-b flex items-center shrink-0 bg-background/60">
            <div className="w-full">
              <div className="min-h-9 w-full rounded-md border border-input bg-background px-2 py-1 flex flex-wrap items-center gap-1.5">
                {inboxSearchTerms.map((term) => (
                  <span
                    key={term}
                    className="inline-flex items-center gap-1 rounded-full border border-input bg-muted text-muted-foreground px-2 py-0.5 text-xs"
                  >
                    {term}
                    <button
                      type="button"
                      className="rounded-full hover:bg-muted-foreground/10 p-0.5"
                      onClick={() => setInboxSearchTerms((prev) => prev.filter((t) => t !== term))}
                      aria-label={`Eliminar búsqueda ${term}`}
                      title="Eliminar chip"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={inboxSearch}
                  onChange={(e) => setInboxSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const term = inboxSearch.trim();
                      if (!term) return;
                      setInboxSearchTerms((prev) => (prev.includes(term) ? prev : [...prev, term]));
                      setInboxSearch("");
                      return;
                    }
                    if (e.key === "Backspace" && !inboxSearch && inboxSearchTerms.length > 0) {
                      setInboxSearchTerms((prev) => prev.slice(0, -1));
                    }
                  }}
                  placeholder={`Buscar en ${folderDisplayName(selectedFolder).toLowerCase()}...`}
                  className="h-7 min-w-[140px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
            </div>
          </div>
          <div ref={listScrollRef} onScroll={handleListScroll} className="min-h-0 overflow-y-auto flex-1 mobile-nav-scroll">
            {loadingMessages ? (
              <div className="p-4 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>
            ) : filteredMessages.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No hay correos en esta carpeta.</div>
            ) : (
              filteredMessages.map((m) => {
                const isChecked = batchSelectedIds.has(m.id);
                const domain = senderDomain(m.from_email, m.from);
                const initials = senderInitials(m.from, m.from_email);
                const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64` : "";
                const toggleBatch = () => setBatchSelectedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
                  return next;
                });
                return (
                  <ContextMenu key={m.id}>
                    <ContextMenuTrigger asChild>
                      <button
                        type="button"
                        className={`w-full text-left border-b last:border-b-0 px-3 py-3 md:py-2 ${
                          batchMode
                            ? isChecked ? "bg-[#f4f4f5]" : "hover:bg-muted/40"
                            : selectedMessageId === m.id ? "bg-[#f4f4f5]" : "hover:bg-muted/40"
                        }`}
                        onClick={() => { if (batchMode) { toggleBatch(); } else { setSelectedMessageId(m.id); setMobilePane("message"); } }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="inline-flex w-4 shrink-0 justify-center items-center">
                            {batchMode ? (
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={toggleBatch}
                                onClick={(e) => e.stopPropagation()}
                                className="h-3.5 w-3.5"
                              />
                            ) : m.unread ? (
                              <span className="w-2 h-2 rounded-full bg-destructive" />
                            ) : (
                              <span className="w-2 h-2" />
                            )}
                          </span>
                          <span
                            className={`relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold ${
                              m.flagged ? "border-orange-300 bg-orange-50 text-orange-700" : "border-input bg-muted text-foreground"
                            }`}
                            title={m.from || m.from_email || ""}
                          >
                            {faviconUrl ? (
                              <img
                                src={faviconUrl}
                                alt={domain || "remitente"}
                                className="h-5 w-5 rounded-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fallback = e.currentTarget.parentElement?.querySelector("[data-fallback-initials]");
                                  if (fallback) fallback.classList.remove("hidden");
                                }}
                              />
                            ) : null}
                            <span data-fallback-initials className={faviconUrl ? "hidden" : ""}>
                              {initials}
                            </span>
                            {m.flagged && (
                              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full border border-orange-300 bg-background p-[1px]">
                                <Pin className="w-2.5 h-2.5 text-orange-500 fill-orange-400" />
                              </span>
                            )}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{m.subject || "(Sin asunto)"}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <span className="truncate flex-1">{m.from || ""}</span>
                              <span className="shrink-0">{formatInboxRightDate(m.date)}</span>
                            </div>
                          </div>
                        </div>
                      </button>
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      <ContextMenuItem onSelect={() => { setSelectedMessageId(m.id); setReplyMode("reply"); setReplyOpen(true); }}>
                        Responder
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => { setSelectedMessageId(m.id); executeAction(m.flagged ? "unflag" : "flag", m.id); }}>
                        {m.flagged ? "Desanclar" : "Anclar"}
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => { setSelectedMessageId(m.id); executeAction("mark_unread", m.id); }}>
                        Marcar como no leído
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => { setSelectedMessageId(m.id); executeAction("archive", m.id); }}>
                        Archivar
                      </ContextMenuItem>
                      <ContextMenuItem
                        onSelect={() => { setSelectedMessageId(m.id); executeAction("trash", m.id); }}
                        className="text-destructive focus:text-destructive"
                      >
                        Eliminar
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                );
              })
            )}
            {loadingMoreMessages && (
              <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Cargando más correos...
              </div>
            )}
            {!hasMoreMessages && !loadingMessages && !loadingMoreMessages && filteredMessages.length > 0 && (
              <div className="py-3 px-4 text-center text-xs text-muted-foreground">
                No hay más correos disponibles.
              </div>
            )}
          </div>
        </aside>

        <main className={`${mobilePane === "message" ? "flex" : "hidden md:flex"} flex-1 min-w-0 flex-col overflow-hidden`}>
          <MobileBackBar onBack={() => setMobilePane("list")} label={folderDisplayName(selectedFolder)} />
          <div className="h-14 border-b px-2 md:px-4 flex items-center gap-2 shrink-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>button]:shrink-0">
            {batchMode ? (
              <>
                <span className="text-sm text-muted-foreground mr-auto">
                  {batchSelectedIds.size > 0
                    ? `${batchSelectedIds.size} seleccionado${batchSelectedIds.size !== 1 ? "s" : ""}`
                    : "Selecciona correos"}
                </span>
                <Button variant="outline" size="icon" onClick={() => executeBatchAction("flag")} disabled={batchSelectedIds.size === 0} aria-label="Anclar" title="Anclar" className="text-orange-500 border-orange-300 hover:text-orange-600">
                  <Pin className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => executeBatchAction("mark_unread")} disabled={batchSelectedIds.size === 0} aria-label="No leído" title="No leído">
                  <ShieldAlert className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => executeBatchAction("archive")} disabled={batchSelectedIds.size === 0} aria-label="Archivar" title="Archivar">
                  <Archive className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => executeBatchAction("trash")} disabled={batchSelectedIds.size === 0} aria-label="Borrar" title="Borrar">
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-1"
                  onClick={() => { setBatchMode(false); setBatchSelectedIds(new Set()); }}
                >
                  Cancelar
                </Button>
              </>
            ) : (
              <>
                <div className="hidden md:block flex-1" />
                <Button variant="outline" size="icon" onClick={() => { setReplyMode("reply"); setReplyOpen(true); }} disabled={!selectedMessage} aria-label="Responder" title="Responder">
                  <Reply className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => { setReplyMode("reply_all"); setReplyOpen(true); }} disabled={!selectedMessage} aria-label="Responder a todos" title="Responder a todos">
                  <ReplyAll className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => { setReplyMode("forward"); setReplyOpen(true); }} disabled={!selectedMessage} aria-label="Reenviar" title="Reenviar">
                  <Forward className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => executeAction("archive")} aria-label="Archivar" title="Archivar">
                  <Archive className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => executeAction("trash")} aria-label="Borrar" title="Borrar">
                  <Trash2 className="w-4 h-4" />
                </Button>
                {(() => {
                  const sel = messages.find((m) => m.id === selectedMessageId);
                  const isPinned = sel?.flagged;
                  return (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => executeAction(isPinned ? "unflag" : "flag")}
                      disabled={!selectedMessage}
                      aria-label={isPinned ? "Desanclar" : "Anclar"}
                      title={isPinned ? "Desanclar" : "Anclar"}
                      className={isPinned ? "text-orange-500 border-orange-300 hover:text-orange-600" : ""}
                    >
                      {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </Button>
                  );
                })()}
                <Button variant="outline" size="icon" onClick={() => executeAction("mark_unread")} aria-label="No leído" title="No leído">
                  <ShieldAlert className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="hidden md:inline-flex ml-1 h-10 bg-black text-white hover:bg-black/90"
                  onClick={() => setComposeOpen(true)}
                  disabled={!selectedAccountId}
                >
                  Nuevo
                </Button>
              </>
            )}
          </div>
          {selectedMessage ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="shrink-0 border-b bg-[#f4f4f5]">
                <button
                  type="button"
                  className="h-14 w-full pl-4 md:pl-6 pr-4 flex items-center justify-between gap-3 text-left"
                  onClick={() => setHeaderExpanded((v) => !v)}
                >
                  <h2 className="text-base font-semibold truncate" title={selectedMessage.subject || "(Sin asunto)"}>
                    {selectedMessage.subject || "(Sin asunto)"}
                  </h2>
                  <div className="shrink-0 flex items-center gap-2">
                    {messageAttachments.length > 0 && (
                      <button
                        type="button"
                        className="inline-flex items-center rounded-full border border-input bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted"
                        onClick={scrollToAttachments}
                        title="Ir a adjuntos"
                      >
                        {messageAttachments.length} adjunto{messageAttachments.length !== 1 ? "s" : ""}
                      </button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHeaderExpanded((v) => !v);
                      }}
                      aria-label={headerExpanded ? "Ocultar detalles" : "Mostrar detalles"}
                      title={headerExpanded ? "Ocultar detalles" : "Mostrar detalles"}
                    >
                      {headerExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </button>
                {headerExpanded && (
                <div className="px-6 pr-4 pb-4 text-sm text-muted-foreground">
                <div>
                  <b>De:</b>{" "}
                  {extractEmails(selectedMessage.from).length > 0 ? (
                    extractEmails(selectedMessage.from).map((email, idx) => (
                      <button
                        key={`${email}-${idx}`}
                        type="button"
                        className="text-blue-600 underline underline-offset-2 hover:text-blue-700 mr-1"
                        onClick={() => copyEmail(email)}
                        title="Click para copiar"
                      >
                        {email}
                      </button>
                    ))
                  ) : (
                    selectedMessage.from || "-"
                  )}
                </div>
                <div>
                  <b>Para:</b>{" "}
                  {toEmails.length > 0 ? (
                    <>
                    {visibleToEmails.map((email, idx) => (
                      <button
                        key={`${email}-${idx}`}
                        type="button"
                        className="text-blue-600 underline underline-offset-2 hover:text-blue-700 mr-1"
                        onClick={() => copyEmail(email)}
                        title="Click para copiar"
                      >
                        {email}
                      </button>
                    ))}
                    {hiddenToCount > 0 && (
                      <button
                        type="button"
                        className="text-blue-600 underline underline-offset-2 hover:text-blue-700 mr-1"
                        onClick={() => setShowAllRecipients(true)}
                        title="Mostrar todos los destinatarios"
                      >
                        +{hiddenToCount}
                      </button>
                    )}
                    </>
                  ) : (
                    selectedMessage.to || "-"
                  )}
                </div>
                  <div><b>Fecha:</b> {formatHeaderDate(selectedMessage.date || "-")}</div>
                </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto overflow-x-auto p-4 md:p-6 mobile-nav-scroll">
                <div
                  className="[&_img]:max-w-full [&_img]:h-auto [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-blue-700"
                  dangerouslySetInnerHTML={{ __html: renderedMessageHtml }}
                />
              </div>
              {messageAttachments.length > 0 && (
                <div ref={attachmentsSectionRef} className="shrink-0 border-t bg-[#f4f4f5] p-4 mobile-nav-scroll">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-sm font-medium">Adjuntos</div>
                    <Button type="button" size="sm" variant="outline" onClick={downloadAllAttachments}>
                      Descargar todo
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {messageAttachments.map((att, idx) => (
                      <div
                        key={`${att.filename || "adjunto"}-${idx}`}
                        className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs select-none"
                        draggable
                        onMouseEnter={() => { if ((att.content_type || "").startsWith("image/")) ensureAttachmentObjectUrl(att).catch(() => {}); }}
                        onDragStart={(e) => {
                          const entry = attachmentObjectUrls[String(att.index)];
                          if (!entry?.url) return;
                          const mime = entry.contentType || att.content_type || "application/octet-stream";
                          const filename = att.filename || "adjunto";
                          // Formato estándar Chromium para arrastrar y descargar al escritorio.
                          e.dataTransfer.setData("DownloadURL", `${mime}:${filename}:${entry.url}`);
                          e.dataTransfer.setData("text/uri-list", entry.url);
                        }}
                      >
                        <button type="button" className="w-10 h-10 rounded border bg-muted/40 overflow-hidden shrink-0" onDoubleClick={() => previewAttachment(att)} title="Doble click para previsualizar">
                          {(att.content_type || "").startsWith("image/") && attachmentObjectUrls[String(att.index)]?.url ? (
                            <img src={attachmentObjectUrls[String(att.index)].url} alt={att.filename || "adjunto"} className="w-full h-full object-cover" />
                          ) : (
                            <span className="inline-flex w-full h-full items-center justify-center text-[10px] text-muted-foreground">
                              {(att.content_type || "").includes("pdf") ? "PDF" : (att.content_type || "").startsWith("video/") ? "VIDEO" : "FILE"}
                            </span>
                          )}
                        </button>
                        <div className="min-w-0">
                          <button
                            type="button"
                            className="font-medium truncate max-w-[180px] text-left hover:underline block"
                            onDoubleClick={() => previewAttachment(att)}
                            title={att.filename || "adjunto"}
                          >
                            {formatAttachmentName(att.filename || "adjunto")}
                          </button>
                          <span className="text-muted-foreground">{formatAttachmentSize(att.size)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">Selecciona un correo para leerlo.</div>
          )}
        </main>
      </div>

      <AddAccountDialog open={addOpen} onOpenChange={setAddOpen} onCreated={reloadAccounts} />
      <ComposeMailDialog open={composeOpen} onOpenChange={setComposeOpen} account={selectedAccount} onSent={reloadAccounts} />
      <ReplyMailDialog
        open={replyOpen}
        onOpenChange={setReplyOpen}
        account={selectedAccount}
        message={selectedMessage}
        mode={replyMode}
        onSent={reloadAccounts}
      />
      <AccountSettingsModal
        account={settingsAccount}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={reloadAccounts}
        onDelete={handleDeleteAccount}
      />
      <Dialog
        open={previewOpen}
        onOpenChange={(o) => {
          setPreviewOpen(o);
          if (!o) setAttachmentPreview(null);
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{attachmentPreview?.filename || "Previsualización"}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-auto">
            {attachmentPreview?.contentType?.startsWith("image/") ? (
              <img src={attachmentPreview.url} alt={attachmentPreview.filename} className="max-w-full h-auto mx-auto" />
            ) : attachmentPreview?.contentType?.startsWith("video/") ? (
              <video src={attachmentPreview.url} controls className="max-w-full h-auto mx-auto" />
            ) : attachmentPreview?.contentType === "application/pdf" ? (
              <iframe src={attachmentPreview.url} title={attachmentPreview.filename} className="w-full h-[70vh] border rounded" />
            ) : (
              <div className="text-sm text-muted-foreground p-4">
                Este tipo de archivo no se puede previsualizar. Usa “Descargar”.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {pendingActionCount > 0 && (
        <div className="fixed inset-0 z-[120] bg-background/45 backdrop-blur-[1px] flex items-center justify-center">
          <div className="rounded-md border bg-background px-4 py-3 shadow-lg flex items-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Procesando acción...
          </div>
        </div>
      )}
    </div>
  );
}
